import {
  Box,
  Card,
  Stack,
  Spread,
  Badge,
  Toggle,
  Text,
} from 'braid-design-system';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 } from 'uuid';

import SkeletonLine from 'src/components/SkeletonLine/SkeletonLine';
import { logger } from 'src/modules/logger';

import { useHomeRecommendations } from '../DashboardRecommendations/useHomeRecommendations';

import { CareerFeedChatArea } from './CareerFeedChatArea/CareerFeedChatArea';
import { createSession, sendMessage } from './api/careerFeedAssistantApi';
import type { CareerFeedMessage } from './types';

const GENERIC_ERROR_MESSAGE =
  'Error occurred while sending the message. Please try again!';

type CareerFeedAIAssistantProps = {
  onToggleSeekAI?: (value: boolean) => void;
};

/** *
 * This component is responsible for displaying the AI Assistant for the Career Feed.
 * This is still in in discovery phase and the implementation of this component might change drastically.
 * The reason why this is in the code base is so that people can start playing around with it
 * and get feedback on the implementation.
 */
export const CareerFeedAIAssistant = ({
  onToggleSeekAI,
}: CareerFeedAIAssistantProps) => {
  const { rawRecommendedJobs, loading, error } = useHomeRecommendations();

  const [isSeekAIEnabled, setSeekAIEnabled] = useState(false);
  const toggleSeekAI = useCallback(() => {
    setSeekAIEnabled(!isSeekAIEnabled);
    onToggleSeekAI?.(!isSeekAIEnabled);
  }, [isSeekAIEnabled, onToggleSeekAI]);

  const [messages, setMessages] = useState<CareerFeedMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const anyMessageLoading = useMemo(
    () => messages.some((message) => message.loading),
    [messages],
  );

  const invokeCreateSession = useCallback(async () => {
    const messageId = v4();
    setMessages((prevMessages) => [
      ...prevMessages,
      { messageId, role: 'assistant', loading: true },
    ]);

    try {
      const result = await createSession({
        jobs:
          rawRecommendedJobs?.globalJobs.map(({ job }) => ({
            title: job.title,
            salaryLabel: job.salary?.label ?? '',
            location: job.location.label,
            listingDate: job.listedAt?.label ?? '',
          })) ?? [],
      });

      setSessionId(result.sessionId);
      setMessages((prevMessages) =>
        prevMessages.map((prevMessage) => {
          if (prevMessage.messageId === messageId) {
            return {
              ...prevMessage,
              loading: false,
              text: result.message,
              suggestions: result.suggestions,
            };
          }

          return prevMessage;
        }),
      );
    } catch (e) {
      logger.error(
        {
          err: e as Error,
        },
        'GenAI: Error occurred while creating the session',
      );
      setMessages((prevMessages) =>
        prevMessages.map((prevMessage) => {
          if (prevMessage.messageId === messageId) {
            return {
              ...prevMessage,
              loading: false,
              text: GENERIC_ERROR_MESSAGE,
            };
          }

          return prevMessage;
        }),
      );
    }
  }, [rawRecommendedJobs?.globalJobs]);

  const invokeSendMessage = useCallback(
    async (message: string) => {
      const assistantMessageId = v4();

      setMessages((prevMessages) => [
        ...prevMessages,
        {
          messageId: v4(),
          role: 'user',
          loading: false,
          text: message,
        },
        {
          messageId: assistantMessageId,
          role: 'assistant',
          loading: true,
        },
      ]);

      if (!sessionId) {
        return;
      }

      try {
        const result = await sendMessage(sessionId, message);
        setMessages((prevMessages) =>
          prevMessages.map((prevMessage) => {
            if (prevMessage.messageId === assistantMessageId) {
              return {
                ...prevMessage,
                loading: false,
                text: result.message,
                suggestions: result.suggestions,
              };
            }

            return prevMessage;
          }),
        );
      } catch (e) {
        logger.error(
          {
            err: e as Error,
          },
          'GenAI: Error occurred while sending the message',
        );
        setMessages((prevMessages) =>
          prevMessages.map((prevMessage) => {
            if (prevMessage.messageId === assistantMessageId) {
              return {
                ...prevMessage,
                loading: false,
                text: GENERIC_ERROR_MESSAGE,
              };
            }

            return prevMessage;
          }),
        );
      }
    },
    [sessionId],
  );

  useEffect(() => {
    if (isSeekAIEnabled && !sessionId) {
      invokeCreateSession();
    }
  }, [invokeCreateSession, isSeekAIEnabled, sessionId]);

  const onSendMessage: (message: string) => void = useCallback(
    (message) => {
      invokeSendMessage(message);
    },
    [invokeSendMessage],
  );

  if (loading) {
    return (
      <Box marginTop="large">
        <Card>
          <Stack space="medium">
            <SkeletonLine type="text" width={200} size="small" />
            <SkeletonLine type="text" width={260} size="xsmall" />
          </Stack>
        </Card>
      </Box>
    );
  }

  if (error || rawRecommendedJobs?.totalCount === 0) {
    return null;
  }

  return (
    <Box marginTop="large">
      <Card>
        <Stack space="medium">
          <Spread alignY="center" space="none">
            <Text weight="strong">Seek AI</Text>
            <Badge tone="promote" weight="strong">
              Beta
            </Badge>
          </Spread>
          <Toggle
            on={isSeekAIEnabled}
            onChange={toggleSeekAI}
            id="seek-ai-toggle"
            label="Your AI Assistant"
            align="justify"
          />
          {isSeekAIEnabled && (
            <CareerFeedChatArea
              messages={messages}
              onSendMessage={onSendMessage}
              isCandidateTextFieldDisabled={!sessionId || anyMessageLoading}
            />
          )}
        </Stack>
      </Card>
    </Box>
  );
};
